<template>
    <div class="chat-navbar mt-2">
        <header class="chat-header px-lg-2 d-flex justify-content-between align-items-start">
            <!-- Toggle Icon -->
            <div
                class="sidebar-toggle d-block d-lg-none text-secondary py-25 px-1 cursor-pointer"
                @click="$emit('update:mq-shall-show-left-sidebar', true)"
            >
                <font-awesome-icon icon="angle-left" size="2x" />
            </div>

            <div class="d-flex flex-grow-1 mx-1" style="min-width: 0">
                <b-avatar
                    :text="resolveInitials(activeChat.contact && activeChat.contact.name)"
                    class="mr-1 badge-minimal round-large d-none d-xl-flex"
                    size="md"
                    variant="tertiary"
                />
                <b-avatar
                    v-b-modal.modal-contact-info
                    :text="resolveInitials(activeChat.contact && activeChat.contact.name)"
                    class="mr-1 cursor-pointer badge-minimal round-large d-block d-xl-none"
                    size="md"
                    variant="tertiary"
                />

                <b-modal
                    id="modal-contact-info"
                    dialog-class="active-chat-sidebar-modal"
                    header-close-variant="light"
                    hide-footer
                    scrollable
                >
                    <ActiveChatSidebarContact
                        :contact-id="activeChat.contact && activeChat.contact.id"
                        :shall-show-active-chat-sidebar-contact="true"
                    />
                </b-modal>

                <div class="text-truncate">
                    <h6 class="text-truncate mb-0">
                        {{
                            (activeChat.contact && activeChat.contact.name) ||
                            `(${$t("entities.contact.contact-unknown")})`
                        }}
                    </h6>
                    <div class="d-flex">
                        <b-img
                            v-b-tooltip.hover.bottom="`${activeChat.channel.name}`"
                            :src="
                                require(`@/assets/images/platforms/` +
                                    resolvePlatformIconSvgSrc(
                                        activeChat.channel.provider,
                                        activeChat.channel.platform
                                    ) +
                                    `.svg`)
                            "
                            width="14"
                        />

                        <div class="text-truncate">
                            <small class="text-secondary ml-25">{{ activeChat.channel.name }}</small>
                        </div>
                    </div>
                </div>
            </div>

            <div class="d-flex align-items-center bv-d-sm-down-none">
                <b-button
                    id="active-chat-assign-button"
                    :disabled="isArchiveChatPending"
                    variant="light"
                    @click="
                        activeChat.lastConversation.status === 'open'
                            ? archiveChat({
                                  id: activeChat.lastConversation.id,
                                  channel: {
                                      id: activeChat.channel.id,
                                      platform: activeChat.channel.platform,
                                  },
                                  identityId: activeChat.identity.id,
                                  status: activeChat.lastConversation.status,
                              })
                            : $emit('unarchive-chat', {
                                  id: activeChat.lastConversation.id,
                                  channelId: activeChat.channel.id,
                                  identityId: activeChat.identity.id,
                                  tags: activeChat.tags,
                                  read: true, // in this case the chat is active, so always read
                                  assignedUserId: activeChat.lastConversation.assignedUserId,
                                  assignedTeamId: activeChat.lastConversation.assignedTeamId,
                                  status: activeChat.lastConversation.status,
                              })
                    "
                >
                    <b-spinner v-if="isArchiveChatPending" small></b-spinner>
                    {{
                        activeChat.lastConversation.status === "open"
                            ? $t("entities.conversation.Archive")
                            : $t("entities.conversation.Unarchive")
                    }}
                </b-button>

                <active-chat-dropdown-assign
                    v-if="activeChat.lastConversation.status !== 'closed'"
                    :active-chat="activeChat"
                    :is-assign-chat-pending="isAssignChatPending"
                    :teams="activeChatTeams"
                    class="ml-2"
                    @assign-chat="$emit('assign-chat', $event)"
                />
            </div>

            <!-- Small screen menu -->
            <span class="d-flex mobile-menu d-md-none text-secondary align-items-start">
                <b-dropdown
                    id="chat-navbar-dropdown"
                    class="m-0"
                    no-caret
                    offset="-2rem"
                    right
                    toggle-class="chat-navbar-dropdown-button"
                    variant="flat-secondary"
                >
                    <template slot="button-content">
                        <font-awesome-icon icon="ellipsis-v" size="xl" />
                    </template>

                    <!-- :disabled="isChatAssignedToMe(activeChat)" -->
                    <b-dropdown-item
                        v-if="activeChat.lastConversation.status !== 'closed'"
                        @click="
                            $emit('assign-chat', {
                                id: activeChat.lastConversation.id,
                                channelId: activeChat.channel.id,
                                identityId: activeChat.lastConversation.identityId,
                                tags: activeChat.tags,
                                userId: getUserData().id,
                                status: activeChat.lastConversation.status,
                                teamId:
                                    activeChat.lastConversation.assignedTeamId &&
                                    isUserPartOfTeam(activeChat.lastConversation.assignedTeamId)
                                        ? activeChat.lastConversation.assignedTeamId
                                        : getRandomUserTeamForChannel(activeChat.channel.id),
                            })
                        "
                    >
                        <font-awesome-icon icon="user" />
                        <span class="ml-75">{{ $t("chat-assign-to-me") }}</span>
                    </b-dropdown-item>
                    <b-dropdown-item v-if="activeChat.lastConversation.status !== 'closed'" v-b-modal.modal-assign-to>
                        <font-awesome-icon icon="user" />
                        <span class="ml-75">{{ $t("Assign") }}</span>
                    </b-dropdown-item>
                    <b-dropdown-item :disabled="isArchiveChatPending" @click="archiveChatShowSidebar()"
                        ><font-awesome-icon icon="box-archive" />
                        <span class="ml-75">{{
                            activeChat.lastConversation.status === "open"
                                ? $t("entities.conversation.Archive")
                                : $t("entities.conversation.Unarchive")
                        }}</span>
                    </b-dropdown-item>
                </b-dropdown>

                <div @click="$emit('toggle-mobile-plugin-bar')">
                    <!--
                        The text E is the apps icon in the saysimple-icon font.
                        We cannot use the apps class because BAvatar will fall back to a default icon if no text is given
                    -->
                    <b-avatar
                        class="badge-minimal round-large mr-50 ml-50 saysimple-icon cursor-pointer"
                        size="md"
                        text="E"
                        variant="success"
                    />
                </div>
            </span>
        </header>

        <!-- Assign to modal (small screens) -->
        <b-modal
            id="modal-assign-to"
            dialog-class="active-chat-sidebar-modal"
            header-close-variant="light"
            hide-footer
            scrollable
        >
            <ActiveChatAssign
                :active-chat="activeChat"
                :is-assign-chat-pending="false"
                :teams="activeChatTeams"
                @assign-chat="assignChat"
            />
        </b-modal>
    </div>
</template>

<script>
import { BButton, BAvatar, BSpinner, BDropdown, BDropdownItem, BImg, VBTooltip } from "bootstrap-vue";
import { computed } from "@vue/composition-api";

// Components
import activeChatDropdownAssign from "./activeChatDropdownAssign";
import ActiveChatSidebarContact from "@/views/components/inbox/active-chat-sidebars/contact";
import ActiveChatAssign from "./activeChatAssign";

// Utils
import { resolveProviderColor, resolvePlatformIcon, resolvePlatformIconSvgSrc } from "@/utils/channels";
import { resolveInitials } from "@/utils/users";
import { getUserData } from "@/auth/getUserData";
import useUsers from "@/composables/useUsers";
import useArchiveChat from "@/composables/v2.1/useArchiveChat";

export default {
    components: {
        BButton,
        BAvatar,
        BSpinner,
        BDropdown,
        BDropdownItem,
        BImg,
        activeChatDropdownAssign,
        ActiveChatSidebarContact,
        ActiveChatAssign,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        activeChat: {
            type: Object,
            required: true,
        },
        isAssignChatPending: {
            type: Boolean,
            required: true,
        },
        isArchiveChatPending: {
            type: Boolean,
            required: true,
        },
    },
    setup(props, { emit }) {
        const { teams, getRandomUserTeamForChannel, isUserPartOfTeam } = useUsers();
        const { archiveChat } = useArchiveChat();

        const assignChat = (args) => {
            emit("assign-chat", args);
        };

        const activeChatTeams = computed(() => {
            return props.activeChat.channel.teamChannels
                .map((teamChannel) => teams.value.find((team) => team.id === teamChannel.teamId))
                .filter((teams) => teams);
        });

        const archiveChatShowSidebar = async () => {
            if (props.activeChat.lastConversation.status !== "open") {
                emit("unarchive-chat", {
                    id: props.activeChat.lastConversation.id,
                    channel: {
                        id: props.activeChat.channel.id,
                        platform: props.activeChat.channel.platform,
                    },
                    identityId: props.activeChat.identity.id,
                    status: props.activeChat.lastConversation.status,
                });

                return;
            }

            await archiveChat({
                id: props.activeChat.lastConversation.id,
                channel: {
                    id: props.activeChat.channel.id,
                    platform: props.activeChat.channel.platform,
                },
                identityId: props.activeChat.identity.id,
            });

            emit("update:mq-shall-show-left-sidebar", true);
        };

        return {
            resolveInitials,
            resolveProviderColor,
            resolvePlatformIcon,
            resolvePlatformIconSvgSrc,
            assignChat,
            getUserData,
            activeChatTeams,
            archiveChat,
            getRandomUserTeamForChannel,
            archiveChatShowSidebar,
            isUserPartOfTeam,
        };
    },
};
</script>
